import React, { useEffect } from "react";

import Icon, {
  CheckOutlined,
  LeftOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import HandShakeIcon from "@src/assets/icons/handshake.svg";
import InfoIcon from "@src/assets/icons/info.svg";
import LogsIcon from "@src/assets/icons/logs.svg";
import { ChangelogTab } from "@src/features/changelog/components/changelog_tab";
import { ConfirmationHistoryTab } from "@src/features/data_confirmation/components/confirmation_history_tab";
import { ProjectToolbar } from "@src/features/resource_plan_v2/components/project_toolbar";
import { ResourcePlanOverviewTab } from "@src/features/resource_plan_v2/components/resource_plan_overview_tab";
import useFeatureToggle from "@src/hooks/use_feature_toggle";
import {
  setIsJointVentureProject,
  setProjectId,
  clearFilterSaveProjectId,
  setIsProjectEnded,
} from "@src/services/resourcePlanSliceV2";
import { clearTableActionState } from "@src/services/resourcePlanTableActionsSlice";
import { useGetProjectQuery } from "@src/services/slices/projectsSlice";
import { useAppDispatch, useAppSelector, RootState } from "@src/setupStore";
import { CustomTabItem } from "@src/types/antd_types";
import { Button, Tabs, Alert } from "antd";
import "./project_page_v2.less";
import dayjs from "dayjs";
import { useNavigate, useParams } from "react-router-dom";

const ProjectPageV2: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const plResourcePlanAuditEnabled: boolean = useFeatureToggle(
    "plResourcePlanAudit"
  );

  const plResourcePlanChangelogEnabled: boolean = useFeatureToggle(
    "plResourcePlanChangelog"
  );

  const { data: project, isFetching: isFetchingProject } =
    useGetProjectQuery(id);

  const isProjectEnded = dayjs().isAfter(dayjs(project?.endDate));

  const {
    projectId,
    resourcePlanTableState: { isTableDisabled },
  } = useAppSelector((state: RootState) => state.resourcePlanSliceV2);

  const dispatch = useAppDispatch();

  const isLoading = isTableDisabled || isFetchingProject;

  const tabItems: CustomTabItem[] = [
    {
      key: "1",
      icon: <Icon component={InfoIcon} />,
      label: "Overview",
      children: (
        <ResourcePlanOverviewTab project={project} isLoading={isLoading} />
      ),
    },
    ...(plResourcePlanChangelogEnabled
      ? [
          {
            key: "2",
            icon: <Icon component={LogsIcon} />,
            label: "Changelog",
            children: <ChangelogTab />,
          },
        ]
      : []),
    ...(plResourcePlanAuditEnabled
      ? [
          {
            key: "3",
            icon: <CheckOutlined />,
            label: "Confirmation History",
            children: <ConfirmationHistoryTab projectId={parseInt(id)} />,
          },
        ]
      : []),
  ];

  useEffect(() => {
    if (projectId !== id) {
      dispatch(clearTableActionState());
      dispatch(clearFilterSaveProjectId());
      dispatch(setProjectId(id));
    }
  }, []);

  useEffect(() => {
    if (project) {
      dispatch(setIsJointVentureProject(project));
      dispatch(setIsProjectEnded(isProjectEnded));
    }
  }, [project]);

  return (
    <div className="project-page-v2">
      <div className="top-bar">
        <div className="top-bar__button-title">
          <Button
            onClick={() => navigate("/projects")}
            disabled={isTableDisabled}
            className="top-bar__back-button"
            icon={<LeftOutlined />}
            data-testid="project-back-button"
          />
          <h2>{project?.name}</h2>
          {project?.jointVenturePartner && (
            <HandShakeIcon data-testid="hand-shake-icon" />
          )}
        </div>
        <ProjectToolbar project={project} isDisabled={isLoading} />
      </div>
      <div>
        {isProjectEnded && (
          <Alert
            className="notification-banner"
            icon={<InfoCircleOutlined />}
            message={
              <span>
                <b>PROJECT TIMELINE HAS ALREADY PASSED</b> - Allocations and
                FTEs from this project are no longer visible; only current and
                future data is shown in the system.
              </span>
            }
            type={"info"}
            showIcon
          />
        )}
      </div>

      {plResourcePlanChangelogEnabled || plResourcePlanAuditEnabled ? (
        <Tabs defaultActiveKey="1" type="card" items={tabItems} />
      ) : (
        <ResourcePlanOverviewTab project={project} isLoading={isLoading} />
      )}
    </div>
  );
};

export default ProjectPageV2;
